/**
 * Shared name sorting for the alphabetical sort controls (DS-3831).
 *
 * Spanish dictionary collation with natural number ordering: numbers inside a
 * name compare as numbers ("2. Inspección" before "10. Cierre"), and accents
 * and capitalization never move a name out of its alphabetical position.
 *
 * The collator is built once at module level. Calling `localeCompare` per
 * comparison builds a new collator on every call, which is measurable on
 * accounts with thousands of records.
 */
const collator = new Intl.Collator('es', { numeric: true });

/** `unsorted` is the order the service returns, which today is creation order. */
export type SortByNameOrder = 'unsorted' | 'asc' | 'desc';

/** Cycle driven by successive clicks on the control: ⇅ → ↑ → ↓ → ⇅. */
const NEXT_SORT_BY_NAME_ORDER: Record<SortByNameOrder, SortByNameOrder> = {
  unsorted: 'asc',
  asc: 'desc',
  desc: 'unsorted',
};

export function getNextSortByNameOrder(order: SortByNameOrder): SortByNameOrder {
  return NEXT_SORT_BY_NAME_ORDER[order];
}

/**
 * Spanish dictionary comparison of two names, tolerant of null and undefined.
 * Internal to this module; exported only so the collation has direct tests.
 */
export function compareNames(a: string | null | undefined, b: string | null | undefined): number {
  return collator.compare(a ?? '', b ?? '');
}

/** Numeric ids only, so the tiebreak is a total order with no collation edge cases. */
function compareIds(a: number, b: number): number {
  return a - b;
}

/**
 * Returns a sorted copy of `items`, or the original array when there is no
 * name ordering to apply.
 *
 * Never mutates the input: the group list lives in the Redux store and nine
 * other components read it, so sorting a screen must not reorder it for them.
 *
 * Ties on the name fall back to the id, and the whole comparison is flipped
 * for `desc`, so descending is the exact reverse of ascending even when two
 * items share a name.
 *
 * The tiebreak is not strictly needed here: the list is fully loaded and
 * Array.prototype.sort is stable. It exists so this util states the same
 * total order the server has to implement in DS-3967, where pagination makes
 * a deterministic tiebreak necessary. Stability there is the server's job; a
 * client comparator cannot change which rows a page contains.
 *
 * `getId` is deliberately narrowed to `number`. Every id this backs is a
 * server-generated integer, and admitting strings reopened a class of bugs
 * the tiebreak exists to close: under numeric collation '2' and '02' tie, and
 * negative ids invert. Widen it only alongside a comparator that stays a
 * total order for whatever is admitted.
 */
export function sortByName<T>(
  items: T[],
  order: SortByNameOrder,
  getName: (item: T) => string | null | undefined,
  getId: (item: T) => number,
): T[] {
  if (order === 'unsorted') return items;

  const direction = order === 'asc' ? 1 : -1;

  return [...items].sort((a, b) => {
    const byName = compareNames(getName(a), getName(b));
    return direction * (byName !== 0 ? byName : compareIds(getId(a), getId(b)));
  });
}
